import {
  esc,
  feeCents,
  formatMoney,
  getPaymentMethods,
  getWithdrawalLimits,
  mountWithdrawForm,
  toPaymentError,
  withdrawalErrorMessage,
} from '../../core/payments';
import type {
  CreateWithdrawalResponse,
  PayoutMethod,
  WithdrawFormHandle,
  WithdrawalLimits,
} from '../../core/payments';
import { t, type Translations } from '../../engine/i18n/i18n';

/**
 * Withdrawal panel for the shop.
 *
 * The form itself (payout method, name, card number / IBAN, amount, fee
 * preview, client-side format checks and the POST) is core's
 * `mountWithdrawForm`, so validation and error copy stay identical to the
 * Settings screen. This module adds what that widget does not show: the
 * balance and limits summary on top, and a result card with the withdrawal id
 * and status afterwards. Card numbers and IBANs never leave the widget.
 */

export interface WithdrawPanelOptions {
  root: HTMLElement;
  onSubmitted?: (res: CreateWithdrawalResponse) => void;
  onError: (err: Error) => void;
}

const STATUS_KEYS: Record<string, keyof Translations> = {
  PENDING: 'shop.withdraw.status.PENDING',
  PROCESSING: 'shop.withdraw.status.PROCESSING',
  APPROVED: 'shop.withdraw.status.APPROVED',
  REJECTED: 'shop.withdraw.status.REJECTED',
  FAILED: 'shop.withdraw.status.FAILED',
};

function statusCopy(status: string): string {
  const key = STATUS_KEYS[status];
  return key ? t(key) : status;
}

export function mountWithdrawPanel(opts: WithdrawPanelOptions): { destroy(): void } {
  const { root } = opts;
  let destroyed = false;
  let form: WithdrawFormHandle | null = null;

  root.innerHTML = `<div class="glass-panel p-4 text-center text-sm text-text-dim" role="status">${t('shop.withdraw.loading')}</div>`;

  function statRow(label: string, value: string, dataAttr: string): string {
    return `
      <div class="flex items-baseline justify-between gap-3">
        <dt class="text-text-dim">${esc(label)}</dt>
        <dd class="text-text tabular-nums text-right" ${dataAttr}>${esc(value)}</dd>
      </div>`;
  }

  function resultHtml(res: CreateWithdrawalResponse): string {
    return `
      <div class="glass-panel p-4 flex flex-col gap-2 border border-success/40" role="status" data-withdraw-result>
        <h3 class="font-heading text-sm text-gold">${t('shop.withdraw.requested')}</h3>
        <p class="text-sm text-text-dim leading-relaxed">${esc(res.message || t('shop.withdraw.received'))}</p>
        <dl class="flex flex-col gap-1 text-sm">
          ${statRow(t('shop.withdraw.status'), statusCopy(res.status), 'data-result-status')}
          ${statRow(t('shop.withdraw.amount'), formatMoney(res.amountCents, res.currency || 'EUR'), '')}
          ${statRow(t('shop.withdraw.fee'), formatMoney(res.feeCents, res.currency || 'EUR'), '')}
          ${statRow(t('shop.withdraw.youReceive'), formatMoney(res.netCents, res.currency || 'EUR'), '')}
          <div class="flex items-baseline justify-between gap-3">
            <dt class="text-text-dim">${t('shop.withdraw.requestId')}</dt>
            <dd class="text-text text-xs break-all text-right select-all" data-result-id>${esc(res.id)}</dd>
          </div>
        </dl>
        <p class="text-[10px] text-text-dim leading-relaxed">${t('shop.withdraw.track')}</p>
      </div>`;
  }

  async function load(): Promise<void> {
    try {
      const [limits, methods] = await Promise.all([getWithdrawalLimits(), getPaymentMethods()]);
      if (destroyed) return;
      paint(limits, methods.withdrawals.methods);
    } catch (err) {
      if (destroyed) return;
      const pe = toPaymentError(err);
      const msg = withdrawalErrorMessage(pe.code, pe.message, pe.details);
      root.innerHTML = `<div class="glass-panel p-4 text-sm text-error" role="alert">${esc(msg)}</div>`;
      opts.onError(new Error(msg));
    }
  }

  function paint(limits: WithdrawalLimits, methods: PayoutMethod[]): void {
    const usable = methods.filter((m) => m.enabled !== false && (!limits.methods || limits.methods.includes(m.id)));
    if (!limits.enabled || usable.length === 0) {
      root.innerHTML = `
        <div class="glass-panel p-4 text-sm text-text-dim leading-relaxed" role="status">
          ${t('shop.withdraw.unavailable')}
        </div>`;
      return;
    }

    const maxNow = Math.min(limits.maxCents, limits.availableCents);
    const feePct = limits.feeBps / 100;
    const canWithdraw = limits.availableCents >= limits.minCents;
    // Worked example at the minimum so the fee is concrete, not just a percentage.
    const exampleFee = feeCents(limits.minCents, limits.feeBps);

    root.innerHTML = `
      <div class="flex flex-col gap-4" data-withdraw>
        <div class="glass-panel p-4 flex flex-col gap-2">
          <h3 class="font-heading text-[11px] text-text-dim uppercase tracking-[0.18em]">${t('shop.withdraw.yourBalance')}</h3>
          <div class="font-heading text-2xl text-gold tabular-nums" data-withdraw-available>${esc(formatMoney(limits.availableCents, 'EUR'))}</div>
          <p class="text-[10px] text-text-dim">${t('shop.withdraw.availableNow')}</p>
          <dl class="flex flex-col gap-1 text-sm mt-1">
            ${statRow(t('shop.withdraw.withdrawable'), formatMoney(limits.withdrawableCents, 'EUR'), 'data-withdraw-withdrawable')}
            ${statRow(t('shop.withdraw.minimum'), formatMoney(limits.minCents, 'EUR'), 'data-withdraw-min')}
            ${statRow(t('shop.withdraw.maxPerRequest'), formatMoney(maxNow, 'EUR'), 'data-withdraw-max')}
            ${statRow(t('shop.withdraw.fee'), t('shop.withdraw.feeValue', { pct: feePct, example: formatMoney(exampleFee, 'EUR') }), 'data-withdraw-fee')}
          </dl>
          ${!canWithdraw ? `<p class="text-xs text-text-dim leading-relaxed" data-withdraw-short>
            ${esc(t('shop.withdraw.short', { min: formatMoney(limits.minCents, 'EUR') }))}</p>` : ''}
          ${limits.capRemainingCents !== null && limits.capRemainingCents < limits.withdrawableCents
            ? `<p class="text-xs text-text-dim leading-relaxed">${esc(t('shop.withdraw.cap', { remaining: formatMoney(limits.capRemainingCents, 'EUR') }))}</p>` : ''}
        </div>
        <div class="glass-panel p-4" data-withdraw-form></div>
        <div data-withdraw-result-host></div>
      </div>`;

    const formEl = root.querySelector('[data-withdraw-form]') as HTMLElement;
    form = mountWithdrawForm(formEl, {
      methods: usable,
      limits,
      onSubmitted: (res) => {
        const host = root.querySelector('[data-withdraw-result-host]');
        if (host) host.innerHTML = resultHtml(res);
        opts.onSubmitted?.(res);
      },
    });
  }

  void load();

  return {
    destroy() {
      destroyed = true;
      form?.destroy();
      form = null;
      root.innerHTML = '';
    },
  };
}
